import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormikProps, FormikValues } from 'formik';
import { Grid, Button } from '@mui/material';
import { format, formatISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  getEventTitles,
  getEventSessions,
  getSessionsDetails,
  getEventProperties,
} from 'modules/events/selectors/eventsSelectors';
import {
  fetchSessionsDetails,
  fetchEventSessions,
  fetchEventTitles,
} from 'modules/events/store/eventsActions';
import { EventSession, SchemaElement, SelectOption } from 'modules/events/types/eventsTypes';
import { eventsLocations } from 'modules/events/constants/eventsLocations';
import { getFormSchema } from './EventEditSchema';
import { FormGenerator } from '../../FormGenerator';

const getSessions = (
  selectedEventId: string,
  eventSessions: Record<string, { data: EventSession[] }>,
) =>
  eventSessions[selectedEventId]?.data?.map(({ id, dateTime }) => ({
    id,
    name: format(dateTime, 'd MMMM yyyy - EEEE h:mm a'),
  }));

type EventEditFormProps = FormikProps<FormikValues> & {
  sessionDetailsState: any[];
};

export const EventEditForm: FC<EventEditFormProps> = ({
  setFieldValue,
  values,
  initialValues,
  errors,
  sessionDetailsState,
}) => {
  const dispatch = useDispatch();

  const [fields, setFields] = useState<SchemaElement[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(initialValues?.event?.id);
  const [sessions, setSessions] = useState<SelectOption[]>();
  const [selectedSessionId, setSelectedSessionId] = useState(initialValues?.session?.id || '');
  const [sessionDetails, setSessionDetails] = sessionDetailsState;

  const properties = useSelector(getEventProperties);
  const eventTitles = useSelector(getEventTitles);
  const eventSessions = useSelector(getEventSessions);
  const sessionsDetails = useSelector(getSessionsDetails);

  const handleUpdate = (name: string, value: unknown) => {
    /* On event input change: filter event titles list by typed phrase */
    if (name === 'event') {
      dispatch(fetchEventTitles(value as string));
    }
  };

  const handleChange = (name: string, value: unknown) => {
    /* On event selection or clear */
    if (name === 'event') {
      /* Reset session since there is no event (or new event) to choose sessions from */
      setFieldValue('session', null);
      setSelectedEventId('');

      /* Update session field based on selected event */
      if (value) {
        const { id } = value as SelectOption;
        dispatch(fetchEventSessions(id));
        setSelectedEventId(id);
      }
    }

    /* On session selection: update fields that can be prefilled with session data */
    if (name === 'session' && value) {
      const { id } = value as SelectOption;
      dispatch(fetchSessionsDetails(id));
      setSelectedSessionId(id);
    }

    /* Proceed with the actual field update */
    setFieldValue(name, value);
  };

  /* *********************************************************************** */
  /*           Fetching sessions for initial event if there is any           */
  /* *********************************************************************** */
  useEffect(() => {
    if (!initialValues?.event?.id) return;

    dispatch(fetchEventSessions(initialValues.event.id));
  }, [initialValues, dispatch]);

  /* *********************************************************************** */
  /*                Filtering event titles by searched phrase                */
  /* *********************************************************************** */
  useEffect(() => {
    setFields(prev =>
      prev.map(field =>
        field.name === 'event'
          ? {
              ...field,
              options: eventTitles.data.map(item => ({ id: item.id, name: item.attributes.title })),
            }
          : field,
      ),
    );
  }, [eventTitles, setFields]);

  /* *********************************************************************** */
  /*                   Setting sessions for selected event                   */
  /* *********************************************************************** */

  useEffect(() => {
    setSessions(getSessions(selectedEventId, eventSessions));
  }, [selectedEventId, eventSessions]);

  useEffect(() => {
    setFields(prev =>
      prev.map(field => (field.name === 'session' ? { ...field, options: sessions } : field)),
    );
  }, [sessions, setFields]);

  /* *********************************************************************** */
  /*          Populating fields with details from selected session           */
  /* *********************************************************************** */

  useEffect(() => {
    setSessionDetails(sessionsDetails[selectedSessionId]);
  }, [selectedSessionId, sessionsDetails, setSessionDetails]);

  useEffect(() => {
    if (!sessionDetails || sessionDetails.isPending) return;

    const { estimated_started_at: timestamp, timezone } = sessionDetails.data.data.attributes;

    setFieldValue(
      'dateTime',
      `${formatISO(utcToZonedTime(timestamp * 1000, timezone)).slice(0, 19)}`,
    );
    setFieldValue('duration', sessionDetails.data.included[0].attributes.estimated_duration);
    setFieldValue('language', sessionDetails.data.included[0].attributes.language);
    setFieldValue('description', sessionDetails.data.included[0].attributes.description);

    const kindId = sessionDetails.data.included[0].attributes.kind as keyof typeof eventsLocations;
    const kindName = eventsLocations[kindId];

    setFieldValue('location', { id: kindId, name: kindName });
  }, [sessionDetails, setFieldValue]);

  /* *********************************************************************** */
  /*           Update select options with properties from backend            */
  /* *********************************************************************** */

  useEffect(() => {
    if (properties.isPending) return;

    setFields(getFormSchema(properties.data));
  }, [properties]);

  return (
    <Form>
      <Grid container spacing={2}>
        <FormGenerator
          fields={fields}
          values={values}
          errors={errors}
          onChange={handleChange}
          onUpdate={handleUpdate}
        />
        <Button variant="contained" color="primary" type="submit">
          Next
        </Button>
      </Grid>
    </Form>
  );
};
